// ============================================================
// CineSync Call Mesh Service
// Handles on-demand, optional P2P audio and video mesh.
// Completely separate from the movie stream to avoid interference.
// Bandwidth-optimized (low resolution/framerate for webcams).
// Hardware resources are immediately stopped and released when muted.
// ============================================================

import { ICE_SERVERS_STUN_ONLY, RTC_CONFIG_BASE } from '@/config/webrtc';

export interface CallMeshCallbacks {
  onRemoteStream: (peerId: string, stream: MediaStream) => void;
  onRemoteStreamRemoved: (peerId: string) => void;
  sendSignal: (
    type: 'CALL_OFFER' | 'CALL_ANSWER' | 'CALL_ICE_CANDIDATE' | 'CALL_STATE',
    targetId: string | undefined,
    payload: unknown
  ) => void;
}

interface PeerNode {
  peerId: string;
  pc: RTCPeerConnection;
  isPolite: boolean;
  isMakingOffer: boolean;
  ignoreOffer: boolean;
  remoteStream: MediaStream;
  pendingIceCandidates: RTCIceCandidateInit[];
}

export class CallMeshService {
  private localParticipantId: string;
  private callbacks: CallMeshCallbacks;
  private peers = new Map<string, PeerNode>();

  private localAudioTrack: MediaStreamTrack | null = null;
  private localVideoTrack: MediaStreamTrack | null = null;
  private localStream: MediaStream = new MediaStream();

  constructor(localParticipantId: string, callbacks: CallMeshCallbacks) {
    this.localParticipantId = localParticipantId;
    this.callbacks = callbacks;
  }

  // ── Hardware Capture: Microphone ──────────────────────────────
  async enableMicrophone(): Promise<boolean> {
    if (this.localAudioTrack) return true;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });

      const track = stream.getAudioTracks()[0];
      if (!track) return false;

      this.localAudioTrack = track;
      this.localStream.addTrack(track);

      // Attach to all peer connections
      this.attachTrackToAllPeers(track);

      // Broadcast state update
      this.broadcastCallState();
      return true;
    } catch (err) {
      console.warn('[CallMesh] Failed to capture microphone:', err);
      throw err;
    }
  }

  disableMicrophone(): void {
    if (!this.localAudioTrack) return;

    this.removeTrackFromAllPeers(this.localAudioTrack);
    this.localStream.removeTrack(this.localAudioTrack);

    // Stop track to turn off microphone hardware
    this.localAudioTrack.stop();
    this.localAudioTrack = null;

    this.broadcastCallState();
  }

  // ── Hardware Capture: Camera ──────────────────────────────────
  async enableCamera(): Promise<boolean> {
    if (this.localVideoTrack) return true;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          width: { ideal: 480, max: 640 },
          height: { ideal: 360, max: 480 },
          frameRate: { ideal: 15, max: 20 },
        },
      });

      const track = stream.getVideoTracks()[0];
      if (!track) return false;

      this.localVideoTrack = track;
      this.localStream.addTrack(track);

      // Attach to all peer connections with bandwidth clamp
      this.attachTrackToAllPeers(track);

      this.broadcastCallState();
      return true;
    } catch (err) {
      console.warn('[CallMesh] Failed to capture camera:', err);
      throw err;
    }
  }

  disableCamera(): void {
    if (!this.localVideoTrack) return;

    this.removeTrackFromAllPeers(this.localVideoTrack);
    this.localStream.removeTrack(this.localVideoTrack);

    // Stop track to extinguish camera hardware indicator
    this.localVideoTrack.stop();
    this.localVideoTrack = null;

    this.broadcastCallState();
  }

  getLocalStream(): MediaStream {
    return this.localStream;
  }

  isMicEnabled(): boolean {
    return !!this.localAudioTrack && this.localAudioTrack.enabled;
  }

  isCamEnabled(): boolean {
    return !!this.localVideoTrack && this.localVideoTrack.enabled;
  }

  // ── Track Attachment & Renegotiation ───────────────────────────
  private attachTrackToAllPeers(track: MediaStreamTrack): void {
    for (const [peerId, node] of this.peers.entries()) {
      const senders = node.pc.getSenders();
      const existing = senders.find((s) => s.track === track || s.track?.kind === track.kind);

      if (existing) {
        existing.replaceTrack(track).catch((err) => {
          console.warn(`[CallMesh] replaceTrack failed for ${peerId}:`, err);
        });
      } else {
        const sender = node.pc.addTrack(track, this.localStream);
        if (track.kind === 'video') {
          this.applyBandwidthLimit(sender);
        }
      }

      this.negotiate(node);
    }
  }

  private removeTrackFromAllPeers(track: MediaStreamTrack): void {
    for (const [, node] of this.peers.entries()) {
      const senders = node.pc.getSenders();
      const sender = senders.find((s) => s.track === track);
      if (sender) {
        try {
          node.pc.removeTrack(sender);
        } catch (e) {
          console.warn('[CallMesh] removeTrack failed:', e);
        }
      }
      this.negotiate(node);
    }
  }

  private applyBandwidthLimit(sender: RTCRtpSender): void {
    try {
      const params = sender.getParameters();
      if (!params.encodings || params.encodings.length === 0) {
        params.encodings = [{}];
      }
      // Low bitrate (150kbps) so 1080p movie streaming is never starved
      params.encodings[0].maxBitrate = 150_000;
      params.encodings[0].maxFramerate = 20;
      sender.setParameters(params).catch(() => {});
    } catch {}
  }

  // ── Peer Mesh Management ──────────────────────────────────────
  getOrCreatePeer(peerId: string): PeerNode {
    let node = this.peers.get(peerId);
    if (node) return node;

    // Perfect negotiation pattern: polite peer rolls back on collisions
    const isPolite = this.localParticipantId.localeCompare(peerId) > 0;
    const pc = new RTCPeerConnection({
      ...RTC_CONFIG_BASE,
      iceServers: ICE_SERVERS_STUN_ONLY,
    });

    const remoteStream = new MediaStream();

    node = {
      peerId,
      pc,
      isPolite,
      isMakingOffer: false,
      ignoreOffer: false,
      remoteStream,
      pendingIceCandidates: [],
    };

    pc.ontrack = (ev) => {
      ev.streams[0]?.getTracks().forEach((track) => {
        if (!node!.remoteStream.getTracks().includes(track)) {
          node!.remoteStream.addTrack(track);
        }
      });

      // If streams[0] wasn't populated, add track directly
      if (ev.track && !node!.remoteStream.getTracks().includes(ev.track)) {
        node!.remoteStream.addTrack(ev.track);
      }

      this.callbacks.onRemoteStream(peerId, node!.remoteStream);

      ev.track.onended = () => {
        node!.remoteStream.removeTrack(ev.track);
        if (node!.remoteStream.getTracks().length === 0) {
          this.callbacks.onRemoteStreamRemoved(peerId);
        }
      };
    };

    pc.onicecandidate = (ev) => {
      if (ev.candidate) {
        this.callbacks.sendSignal('CALL_ICE_CANDIDATE', peerId, {
          candidate: ev.candidate.toJSON(),
          targetId: peerId,
        });
      }
    };

    pc.onnegotiationneeded = () => {
      this.negotiate(node!);
    };

    // If we already have local active tracks, add them to this new peer
    this.localStream.getTracks().forEach((track) => {
      const sender = pc.addTrack(track, this.localStream);
      if (track.kind === 'video') {
        this.applyBandwidthLimit(sender);
      }
    });

    this.peers.set(peerId, node);
    return node;
  }

  removePeer(peerId: string): void {
    const node = this.peers.get(peerId);
    if (!node) return;

    this.callbacks.onRemoteStreamRemoved(peerId);
    node.pc.close();
    this.peers.delete(peerId);
  }

  private async negotiate(node: PeerNode): Promise<void> {
    try {
      node.isMakingOffer = true;
      const offer = await node.pc.createOffer();
      if (node.pc.signalingState !== 'stable') return;

      await node.pc.setLocalDescription(offer);
      this.callbacks.sendSignal('CALL_OFFER', node.peerId, {
        sdp: offer,
        targetId: node.peerId,
      });
    } catch (err) {
      console.warn(`[CallMesh] Negotiation offer failed for ${node.peerId}:`, err);
    } finally {
      node.isMakingOffer = false;
    }
  }

  // ── Ingest Remote Signaling ───────────────────────────────────
  async handleOffer(fromPeerId: string, sdp: RTCSessionDescriptionInit): Promise<void> {
    const node = this.getOrCreatePeer(fromPeerId);
    const offerCollision =
      node.isMakingOffer || node.pc.signalingState !== 'stable';

    node.ignoreOffer = !node.isPolite && offerCollision;
    if (node.ignoreOffer) {
      console.warn(`[CallMesh] Offer collision ignored for ${fromPeerId}`);
      return;
    }

    try {
      await node.pc.setRemoteDescription(sdp);
      await this.drainPendingCandidates(node);

      const answer = await node.pc.createAnswer();
      await node.pc.setLocalDescription(answer);

      this.callbacks.sendSignal('CALL_ANSWER', fromPeerId, {
        sdp: answer,
        targetId: fromPeerId,
      });
    } catch (err) {
      console.warn(`[CallMesh] handleOffer failed for ${fromPeerId}:`, err);
    }
  }

  async handleAnswer(fromPeerId: string, sdp: RTCSessionDescriptionInit): Promise<void> {
    const node = this.peers.get(fromPeerId);
    if (!node) return;

    try {
      await node.pc.setRemoteDescription(sdp);
      await this.drainPendingCandidates(node);
    } catch (err) {
      console.warn(`[CallMesh] handleAnswer failed for ${fromPeerId}:`, err);
    }
  }

  async handleIceCandidate(fromPeerId: string, candidate: RTCIceCandidateInit): Promise<void> {
    const node = this.getOrCreatePeer(fromPeerId);

    if (!node.pc.remoteDescription) {
      node.pendingIceCandidates.push(candidate);
      return;
    }

    try {
      await node.pc.addIceCandidate(candidate);
    } catch (err) {
      if (!node.ignoreOffer) {
        console.warn(`[CallMesh] addIceCandidate failed for ${fromPeerId}:`, err);
      }
    }
  }

  private async drainPendingCandidates(node: PeerNode): Promise<void> {
    while (node.pendingIceCandidates.length > 0) {
      const cand = node.pendingIceCandidates.shift();
      if (cand) {
        try {
          await node.pc.addIceCandidate(cand);
        } catch {}
      }
    }
  }

  private broadcastCallState(): void {
    this.callbacks.sendSignal('CALL_STATE', undefined, {
      participantId: this.localParticipantId,
      micEnabled: this.isMicEnabled(),
      camEnabled: this.isCamEnabled(),
    });
  }

  // ── Clean Up All Resources ─────────────────────────────────────
  close(): void {
    this.disableMicrophone();
    this.disableCamera();

    for (const [peerId, node] of this.peers.entries()) {
      this.callbacks.onRemoteStreamRemoved(peerId);
      node.pc.close();
    }
    this.peers.clear();
  }
}

